import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BellRing,
  ChartColumn,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  History,
  House,
  MessageCircle,
  ShieldCheck,
  Undo2,
} from "lucide-react";
import CsvColumnPreview from "@/components/CsvColumnPreview";
import CsvDropzone from "@/components/CsvDropzone";
import ImportStepBar from "@/components/ImportStepBar";
import ProspectImportReview from "@/components/ProspectImportReview";
import { chunk, csvToObjects } from "@/helpers/csvRows";
import { mergePreviews, offsetSkippedRows } from "@/helpers/importSummary";
import { previewCsvRows } from "@/services/importsService";
import {
  useCancelImport,
  useCommitImport,
  useRecentImports,
  useStageImport,
  useUndoImport,
} from "@/hooks/useImportBatches";
import { pageURLs } from "@/constants";
import "./ProspectImport.scss";

const CHUNK = 200;
const PAST = "mls_past_client";
const CSV = "csv";

const PLAYBOOK = [
  {
    icon: ChartColumn,
    title: "Check demand insights",
    line: "See how many Buyer Want-Listings match each home, and at what budgets.",
  },
  {
    icon: FileText,
    title: "Pull a Buyer Match Report",
    line: "Real buyer demand for their home, ready to share.",
  },
  {
    icon: MessageCircle,
    title: "Start the conversation",
    line: "Reach out with the report and a real reason to talk about their next move.",
  },
  {
    icon: BellRing,
    title: "Stay top of mind",
    line: "We alert you when new Buyer Want-Listings match their home.",
  },
];

const GUIDES = {
  [PAST]: {
    icon: House,
    name: "Past buyers",
    line: "From your MLS records",
    title: "Some past buyers could be your next\u00a0sellers",
    points: [
      { icon: House, text: "We find the properties you helped buyers purchase in your MLS records." },
      { icon: ChartColumn, text: "The BLS tracks demand insights on each one, so you don't have to." },
      { icon: BellRing, text: "When Buyer Want-Listings match, you get an alert and a reason to reach out." },
    ],
    privacy: "Nothing is sent to your past clients. Only you see these properties.",
    go: "Find my past buyers",
  },
  [CSV]: {
    icon: FileSpreadsheet,
    name: "CRM contacts",
    line: "A CSV from any CRM",
    title: "Sort your CRM contacts in one\u00a0step",
    points: [
      { icon: House, text: "Sellers and owners become prospect properties with demand insights." },
      { icon: FileText, text: "Buyers become draft Buyer Want-Listings. Nothing is published until you do." },
      { icon: Undo2, text: "You review every row first and can undo the whole import." },
    ],
    privacy: "Nothing is sent to your contacts or written back to your CRM.",
    go: "Choose a CSV file",
  },
};

const plural = (n, one, many) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

const summaryChips = (summary) =>
  [
    summary?.prospects ? plural(summary.prospects, "prospect property", "prospect properties") : null,
    summary?.drafts ? plural(summary.drafts, "draft Buyer Want-Listing", "draft Buyer Want-Listings") : null,
    summary?.clients ? plural(summary.clients, "client", "clients") : null,
  ].filter(Boolean);

const describeRecent = (batch) => {
  const what = summaryChips(batch.summary).join(", ") || "Nothing added";
  const from = batch.source === PAST ? "Past buyers from your MLS" : batch.fileName || "CSV import";
  return { from, what };
};

const isCsv = (file) => /\.csv$/i.test(file?.name || "") || file?.type === "text/csv";

const ProspectImport = ({ show, onHide, initialSource = null, initialFile = null, showPastClients = true }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState("choose");
  const [source, setSource] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState(null);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [confirmRecentUndo, setConfirmRecentUndo] = useState(null);
  // Set once a discard on close was refused, so a second close always gets the user out.
  const forceClose = useRef(false);
  const loadId = useRef(0);
  // Bumped whenever the modal resets or closes, so a stage request still in flight can tell it is orphaned.
  const session = useRef(0);
  // Set synchronously: two quick clicks both run before `commit.isPending` re-renders.
  const committing = useRef(false);
  const inputRef = useRef(null);
  const stage = useStageImport();
  const commit = useCommitImport();
  const undo = useUndoImport();
  const cancel = useCancelImport();
  const recent = useRecentImports(show);

  // Bumping the id makes any file read still in flight stale, so it can't touch state.
  const abandonLoad = () => {
    loadId.current += 1;
    setChecking(false);
  };

  const reset = () => {
    session.current += 1;
    forceClose.current = false;
    abandonLoad();
    setStep("choose");
    setSource(null);
    setFile(null);
    setPreview(null);
    setBatch(null);
    setError(null);
    setConfirmUndo(false);
    setConfirmRecentUndo(null);
  };

  // Runs when the modal opens; a changed initialSource/initialFile mid-session is ignored on purpose.
  useEffect(() => {
    if (!show) return;
    reset();
    if (initialFile) {
      setSource(CSV);
      loadFile(initialFile);
    } else if (initialSource) {
      setSource(initialSource);
    }
  }, [show]);

  // Cancelling is refused once part of a confirm has already added records, so the user has to hear about it.
  const discard = async (batchId) => {
    try {
      await cancel.mutateAsync(batchId);
      return true;
    } catch (err) {
      setError(err?.message || "We couldn't discard this review. Please try again.");
      return false;
    }
  };

  const close = async () => {
    if (commit.isPending) return;
    if (batch?.status === "staged" && !forceClose.current && !(await discard(batch.id))) {
      forceClose.current = true;
      return;
    }
    session.current += 1;
    abandonLoad();
    onHide();
  };

  // Leaving a step orphans any stage request still running for it.
  const goTo = (next) => {
    session.current += 1;
    setError(null);
    setStep(next);
  };

  const leaveReview = async (next) => {
    if (commit.isPending) return;
    if (!(await discard(batch.id))) return;
    setBatch(null);
    next();
  };

  const pick = (next) => {
    // Orphan any stage request still running, so a late result can't open under the wrong source.
    session.current += 1;
    setSource(next);
    setError(null);
    setStep("choose");
  };

  const loadFile = async (picked) => {
    const id = loadId.current + 1;
    loadId.current = id;
    const isCurrent = () => loadId.current === id;
    setError(null);
    setPreview(null);
    if (!picked) return;
    if (!isCsv(picked)) {
      setStep("upload");
      setError("That file isn't a CSV. Export your contacts as a CSV file and try again.");
      return;
    }
    let parsed;
    try {
      parsed = csvToObjects(await picked.text());
    } catch {
      if (isCurrent()) {
        setStep("upload");
        setError("We couldn't read that file. Please try again.");
      }
      return;
    }
    if (!isCurrent()) return;
    if (!parsed.rows.length) {
      setStep("upload");
      setError("We couldn't find any rows in that file.");
      return;
    }
    setFile({ name: picked.name, ...parsed });
    setStep("columns");
    setChecking(true);
    try {
      const previews = [];
      for (const [index, rows] of chunk(parsed.rows, CHUNK).entries()) {
        const result = await previewCsvRows(rows);
        if (!isCurrent()) return;
        previews.push(offsetSkippedRows(result, index * CHUNK + 1));
      }
      setPreview(mergePreviews(previews));
    } catch (err) {
      if (isCurrent()) setError(err?.message || "We couldn't read that file. Please try again.");
    } finally {
      if (isCurrent()) setChecking(false);
    }
  };

  const startReview = async (from = source) => {
    const startedIn = session.current;
    setError(null);
    try {
      const staged = await stage.mutateAsync(
        from === PAST
          ? { source: PAST }
          : { source: CSV, chunks: chunk(file.rows, CHUNK), fileName: file.name },
      );
      if (startedIn !== session.current) {
        // The modal was closed while this was staging. Nobody can review it, so don't leave it behind.
        cancel.mutateAsync(staged.id).catch(() => {});
        return;
      }
      setBatch(staged);
      setStep("review");
    } catch (err) {
      if (startedIn === session.current) setError(err?.message || "Something went wrong. Please try again.");
    }
  };

  const confirm = async () => {
    if (committing.current) return;
    committing.current = true;
    setError(null);
    try {
      setBatch(await commit.mutateAsync(batch.id));
      setStep("done");
    } catch (err) {
      setError(err?.message || "We couldn't finish adding these. Please try again.");
    } finally {
      committing.current = false;
    }
  };

  const runUndo = async (batchId) => {
    setError(null);
    try {
      const undone = await undo.mutateAsync(batchId);
      if (batch?.id === batchId) setBatch(undone);
      setConfirmUndo(false);
      setConfirmRecentUndo(null);
    } catch (err) {
      setError(err?.message || "We couldn't undo that import. Please try again.");
    }
  };

  const openPage = (path) => {
    onHide();
    navigate(path);
  };

  const sources = showPastClients ? [PAST, CSV] : [CSV];
  const active = sources.includes(source) ? source : sources[0];
  const guide = GUIDES[active];
  const csvSteps = ["Upload", "Check columns", "Review", "Done"];
  const pastSteps = ["Review", "Done"];
  const stepNames = source === PAST ? pastSteps : csvSteps;
  const stepIndex = {
    upload: 0,
    columns: 1,
    review: source === PAST ? 0 : 2,
    done: source === PAST ? 1 : 3,
  }[step];

  // On the choose step the error sits under the button that caused it, not below the recent imports.
  const errorNote = (
    <p className="pi-error" role="alert">
      {error}
    </p>
  );

  const recentBatches = (recent.data || []).slice(0, 4);
  const undone = batch?.status === "undone";
  const addedChips = summaryChips(batch?.summary);

  return (
    <Modal
      show={show}
      onHide={close}
      centered
      size={step === "review" ? "xl" : "lg"}
      backdrop={step === "review" ? "static" : true}
      keyboard={step !== "review"}
      className="prospect-import"
      dialogClassName={step === "choose" ? "pi-dialog-choose" : undefined}
      aria-labelledby="prospect-import-title"
    >
      <Modal.Body>
        <button type="button" className="btn-close" aria-label="Close" disabled={commit.isPending} onClick={close}></button>

        {step === "choose" ? (
          <div className="pi-choose">
            <h2 id="prospect-import-title" className="pi-title">
              Sync your clients
            </h2>
            <p className="pi-lead">Review everything before it is added, and undo any import.</p>
            <div className={`pi-source${sources.length > 1 ? "" : " is-single"}`}>
              {sources.length > 1 ? (
                <div className="pi-source__list" role="group" aria-label="Where your clients are">
                  {sources.map((id) => {
                    const { icon: Icon, name, line } = GUIDES[id];
                    return (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={active === id}
                        disabled={stage.isPending}
                        className={`pi-source__item${active === id ? " is-active" : ""}`}
                        onClick={() => pick(id)}
                      >
                        <span className="pi-source__icon" aria-hidden="true">
                          <Icon size={20} strokeWidth={1.8} />
                        </span>
                        <span className="pi-source__text">
                          <strong>{name}</strong>
                          <span>{line}</span>
                        </span>
                        <ChevronRight className="pi-source__chevron" size={16} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <section className="pi-source__detail" aria-label={guide.name}>
                <h3>{guide.title}</h3>
                <ul className="pi-points">
                  {guide.points.map(({ icon: Icon, text }) => (
                    <li key={text}>
                      <span className="pi-points__icon" aria-hidden="true">
                        <Icon size={16} strokeWidth={1.9} />
                      </span>
                      <span>{text.replace(/ (\S+)$/, "\u00a0$1")}</span>
                    </li>
                  ))}
                </ul>
                <p className="pi-privacy">
                  <ShieldCheck size={16} aria-hidden="true" /> {guide.privacy}
                </p>
                <button
                  type="button"
                  className="btn btn-primary pi-source__go"
                  disabled={stage.isPending}
                  onClick={() => {
                    setSource(active);
                    if (active === PAST) startReview(PAST);
                    else goTo("upload");
                  }}
                >
                  {active === PAST && stage.isPending ? "Looking up your past buyers" : guide.go}
                </button>
                {error ? errorNote : null}
              </section>
            </div>
            {recentBatches.length ? (
              <div className="pi-recent">
                <h3>
                  <History size={16} aria-hidden="true" /> Recent imports
                </h3>
                <ul>
                  {recentBatches.map((item) => {
                    const { from, what } = describeRecent(item);
                    return (
                      <li key={item.id}>
                        <div>
                          <strong>{from}</strong>
                          <span>{item.status === "undone" ? "Undone" : what}</span>
                        </div>
                        {item.status === "committed" && confirmRecentUndo === item.id ? (
                          <div className="pi-undo">
                            <span>Remove everything this import added? Clients and published Buyer Want-Listings stay.</span>
                            <button type="button" className="pi-link" onClick={() => setConfirmRecentUndo(null)}>
                              Keep it
                            </button>
                            <button
                              type="button"
                              className="pi-link pi-link--danger"
                              disabled={undo.isPending}
                              onClick={() => runUndo(item.id)}
                            >
                              {undo.isPending ? "Undoing" : "Undo import"}
                            </button>
                          </div>
                        ) : item.status === "committed" ? (
                          <button
                            type="button"
                            className="pi-link"
                            disabled={undo.isPending}
                            onClick={() => setConfirmRecentUndo(item.id)}
                          >
                            Undo
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {step !== "choose" ? <ImportStepBar steps={stepNames} current={stepIndex} /> : null}

        {step === "upload" ? (
          <div className="pi-upload">
            <h2 id="prospect-import-title" className="pi-title">
              Upload your CSV
            </h2>
            <p className="pi-lead">
              Include a column that says buyer, seller or both. Use the template if you are not sure.
            </p>
            <CsvDropzone inputRef={inputRef} onFile={loadFile} />
            <div className="pi-actions">
              <button type="button" className="pi-back" onClick={() => goTo("choose")}>
                <ArrowLeft size={16} aria-hidden="true" /> Back
              </button>
            </div>
          </div>
        ) : null}

        {step === "columns" ? (
          <div className="pi-columns">
            <h2 id="prospect-import-title" className="pi-title">
              Check the columns we read
            </h2>
            {checking ? (
              <p className="pi-checking" role="status">
                <span className="pi-bar" /> Reading {file?.name}
              </p>
            ) : null}
            {preview ? (
              <CsvColumnPreview
                fileName={file?.name}
                preview={preview}
                onChangeFile={() => {
                  abandonLoad();
                  setFile(null);
                  setPreview(null);
                  goTo("upload");
                }}
              />
            ) : null}
            {file?.truncated ? <p className="pi-note">Only the first 2,000 rows are read.</p> : null}
            <div className="pi-actions">
              <button
                type="button"
                className="pi-back"
                onClick={() => {
                  abandonLoad();
                  goTo("upload");
                }}
              >
                <ArrowLeft size={16} aria-hidden="true" /> Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!preview || stage.isPending}
                onClick={() => startReview()}
              >
                {stage.isPending ? "Preparing your review" : `Review ${plural(file?.rows?.length || 0, "row", "rows")}`}
              </button>
            </div>
          </div>
        ) : null}

        {step === "review" && batch ? (
          <ProspectImportReview
            batch={batch}
            onChange={setBatch}
            onBack={() => leaveReview(() => goTo(source === PAST ? "choose" : "columns"))}
            onConfirm={confirm}
            confirming={commit.isPending}
            onTryCsv={() => leaveReview(() => pick(CSV))}
          />
        ) : null}

        {step === "done" && batch ? (
          <div className="pi-done">
            <h2 id="prospect-import-title" className="pi-title">
              {undone ? "Import undone" : addedChips.length ? "Added to your BLS" : "Nothing new was added"}
            </h2>
            {undone ? (
              <p className="pi-lead">
                We removed what this import added. Clients stay in My Clients
                {batch.summary?.undone?.keptPublished
                  ? `, and ${plural(batch.summary.undone.keptPublished, "Buyer Want-Listing", "Buyer Want-Listings")} you already published stay live`
                  : ""}
                .
              </p>
            ) : (
              <>
                <ul className="pi-chips">
                  {addedChips.map((chip) => (
                    <li key={chip}>{chip}</li>
                  ))}
                </ul>
                {batch.summary?.alreadyThere ? (
                  <p className="pi-note">
                    {plural(batch.summary.alreadyThere, "row was", "rows were")} already in the BLS and left as they were.
                  </p>
                ) : null}
                <div className="pi-links">
                  {batch.summary?.prospects ? (
                    <button type="button" className="pi-option pi-option--compact" onClick={() => openPage(pageURLs.agentInventoryListing)}>
                      <span className="pi-option__title">See your prospect properties</span>
                      <span className="pi-option__line">Open any one to see its demand insights and pull a report.</span>
                    </button>
                  ) : null}
                  {batch.summary?.drafts ? (
                    <button type="button" className="pi-option pi-option--compact" onClick={() => openPage(pageURLs.agentMyWantAdListings)}>
                      <span className="pi-option__title">Review your draft Buyer Want-Listings</span>
                      <span className="pi-option__line">They wait in Ready to review until you publish them.</span>
                    </button>
                  ) : null}
                  {batch.summary?.clients ? (
                    <button type="button" className="pi-option pi-option--compact" onClick={() => openPage(pageURLs.agentMyClients)}>
                      <span className="pi-option__title">See your clients</span>
                      <span className="pi-option__line">Add details any time to draft a Buyer Want-Listing.</span>
                    </button>
                  ) : null}
                </div>
                <section className="pi-card pi-card--playbook pi-card--inline">
                  <h3>What to do next</h3>
                  <ul className="pi-playbook">
                    {PLAYBOOK.slice(0, 3).map(({ icon: Icon, title, line }) => (
                      <li key={title}>
                        <span className="pi-playbook__icon" aria-hidden="true">
                          <Icon size={18} strokeWidth={1.8} />
                        </span>
                        <div>
                          <strong>{title}</strong>
                          <span>{line}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              </>
            )}
            <div className="pi-actions">
              {!undone ? (
                confirmUndo ? (
                  <div className="pi-undo">
                    <span>Remove everything this import added? Clients and published Buyer Want-Listings stay.</span>
                    <button type="button" className="pi-link" onClick={() => setConfirmUndo(false)}>
                      Keep it
                    </button>
                    <button
                      type="button"
                      className="pi-link pi-link--danger"
                      disabled={undo.isPending}
                      onClick={() => runUndo(batch.id)}
                    >
                      {undo.isPending ? "Undoing" : "Undo import"}
                    </button>
                  </div>
                ) : (
                  <button type="button" className="pi-link" onClick={() => setConfirmUndo(true)}>
                    Undo this import
                  </button>
                )
              ) : (
                <span />
              )}
              <button type="button" className="btn btn-primary" onClick={close}>
                Done
              </button>
            </div>
          </div>
        ) : null}

        {error && step !== "choose" ? errorNote : null}
      </Modal.Body>
    </Modal>
  );
};

export default ProspectImport;
